import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, LayoutGrid, X, Moon, Sun, User, LogOut, AlertCircle, AlertTriangle, Info, Trash2, CheckCheck, RefreshCw, Package } from "lucide-react";
import { UserData } from "./LoginPage";
import { Module, OpenModule } from "@/types/modules";
import { FrontendNotification, fallbackModuleRouteForNotificationType } from "@/lib/notifications";
import { notificationsAPI, usuariosAPI, systemConfigAPI, reportesAPI, getMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { notificationsLiveStore } from "@/lib/notificationsLiveStore";
import { dataRefresh } from "@/lib/dataRefresh";
import { prefetchModuleChunkByRoute } from "@/lib/moduleChunkPrefetch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function withAvatarCacheBust(mediaUrl: string) {
  const sep = mediaUrl.includes("?") ? "&" : "?";
  return `${mediaUrl}${sep}_absv=${Date.now()}`;
}

interface NavbarProps {
  user: UserData;
  onLogout: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  openModules: OpenModule[];
  onCloseModule: (moduleId: number) => void;
  onReorderModules: (newOrder: OpenModule[]) => void;
  onViewPerfil: () => void;
  /** Cierra la pantalla de perfil al navegar desde la barra (evita que PerfilPage siga tapando módulos). */
  onDismissPerfil?: () => void;
  openModuleByRoute?: (route: string) => void;
  /** Mismos módulos permitidos que en la home (rol / permisos). */
  permittedModules: Module[];
}

// Unified Notification type for display
type DisplayNotification = FrontendNotification & { isPersistent?: boolean };

function notificationIdStr(n: DisplayNotification): string {
  return String(n.id);
}

/** Campana: API no leídas siempre; dinámicas solo si no están descartadas en localStorage. */
function showNotificationInBell(n: DisplayNotification, seenIds: string[]): boolean {
  if (n.isPersistent) return true;
  return !seenIds.includes(notificationIdStr(n));
}

/** Tras vaciar stock, el id dinámico puede repetirse (mismo conteo/firma); limpiar "vistas" para que vuelva la alerta. */
function clearSeenStockDynamicIds(setter: React.Dispatch<React.SetStateAction<string[]>>) {
  setter((prev) => {
    const next = prev.filter(
      (id) =>
        !String(id).startsWith("stock-cero-dynamic-") &&
        !String(id).startsWith("stock-bajo-dynamic-")
    );
    if (next.length !== prev.length) {
      localStorage.setItem("seenNotificationIds", JSON.stringify(next));
    }
    return next;
  });
}

/**
 * Destructive demo actions are opt-in at build time. Preview/prod and an
 * unconfigured local Vite build omit them.
 */
const demoResetFlag = import.meta.env.VITE_SHOW_DEMO_RESET_ACTIONS;
const showDemoResetActions = demoResetFlag === "true" || demoResetFlag === "1";

const START_CLICK_DEBOUNCE_MS = 280;

export function Navbar({ user, onLogout, isDarkMode, toggleDarkMode, openModules, onCloseModule, onReorderModules, onViewPerfil, onDismissPerfil, openModuleByRoute, permittedModules }: NavbarProps) {
  const [location, navigate] = useLocation();
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const startMenuTriggerRef = useRef<HTMLDivElement | null>(null);
  const startMenuPanelRef = useRef<HTMLDivElement | null>(null);
  const startClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startClickCountRef = useRef(0);
  const [draggedModuleIndex, setDraggedModuleIndex] = useState<number | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<DisplayNotification[]>([]);
  const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showVaciateStockConfirm, setShowVaciateStockConfirm] = useState(false);
  const [showResetCatalogConfirm, setShowResetCatalogConfirm] = useState(false);
  const [isStockOpLoading, setIsStockOpLoading] = useState(false);
  const userAvatarUrl = user.avatar_url || user.avatarUrl || null;

  // Avatar: currentUser revalidado es fuente primaria; dataRefresh cubre cambios same-tab.
  useEffect(() => {
    const loadAvatar = async (bustCache: boolean) => {
      try {
        if (userAvatarUrl) {
          const base = getMediaUrl(userAvatarUrl);
          setCustomAvatarUrl(base ? (bustCache ? withAvatarCacheBust(base) : base) : null);
          return;
        }

        const result = await usuariosAPI.getAvatar(user.username) as {
          avatar_url: string | null;
          avatarUrl?: string | null;
        };
        const avatarUrl = result.avatar_url || result.avatarUrl;
        if (avatarUrl) {
          const base = getMediaUrl(avatarUrl);
          setCustomAvatarUrl(base ? (bustCache ? withAvatarCacheBust(base) : base) : null);
        } else {
          setCustomAvatarUrl(null);
        }
      } catch (err) {
        console.error("Failed to load avatar:", err);
      }
    };

    loadAvatar(false);

    const onUserAvatarChanged = () => {
      void loadAvatar(true);
    };
    dataRefresh.on("userAvatar", onUserAvatarChanged);
    return () => dataRefresh.off("userAvatar", onUserAvatarChanged);
  }, [user.username, userAvatarUrl]);

  const [seenNotificationIds, setSeenNotificationIds] = useState<string[]>(() => {
    const saved = localStorage.getItem("seenNotificationIds");
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const sync = () => {
      const snapshot = notificationsLiveStore.getSnapshot();
      setNotifications(snapshot.items);
    };

    const unsubscribe = notificationsLiveStore.subscribe(sync);
    sync();
    return () => {
      unsubscribe();
    };
  }, []);

  const bellNotifications = notifications.filter((n) =>
    showNotificationInBell(n, seenNotificationIds)
  );
  const hasUnseenNotifications = bellNotifications.length > 0;

  // Sync seenNotificationIds with current notifications: 
  // remove IDs from seenNotificationIds that are no longer in notifications list
  // this is a cleanup to prevent the array from growing indefinitely
  useEffect(() => {
    if (notifications.length > 0) {
      const currentIds = new Set(notifications.map((n) => notificationIdStr(n)));
      const filteredSeen = seenNotificationIds.filter((id) => currentIds.has(String(id)));
      if (filteredSeen.length !== seenNotificationIds.length) {
        setSeenNotificationIds(filteredSeen);
        localStorage.setItem("seenNotificationIds", JSON.stringify(filteredSeen));
      }
    }
  }, [notifications]);
  const getSeverityIcon = (severity: DisplayNotification['severity']) => {
    switch (severity) {
      case 'high':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const handleNotificationClick = (notification: DisplayNotification) => {
    const targetRoute =
      notification.moduleRoute ||
      fallbackModuleRouteForNotificationType(notification.type);

    if (targetRoute) {
      if (openModuleByRoute) {
        openModuleByRoute(targetRoute);
      } else {
        onDismissPerfil?.();
        navigate(targetRoute);
      }
    }
    setNotificationsOpen(false);

    if (notification.isPersistent) {
      void (async () => {
        try {
          const nid = Number.parseInt(notificationIdStr(notification), 10);
          if (!Number.isNaN(nid)) {
            await notificationsAPI.markRead(nid);
          }
          dataRefresh.emit("notifications");
        } catch (err) {
          console.error("Failed to mark notification as read", err);
        }
      })();
    } else {
      const sid = notificationIdStr(notification);
      setSeenNotificationIds((prev) => {
        if (prev.includes(sid)) return prev;
        const next = [...prev, sid];
        localStorage.setItem("seenNotificationIds", JSON.stringify(next));
        return next;
      });
    }
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notification: DisplayNotification) => {
    e.preventDefault();
    e.stopPropagation();
    if (notification.isPersistent) {
      try {
        await notificationsAPI.markRead(parseInt(notification.id));
        dataRefresh.emit("notifications");
      } catch (err) {
        console.error(err);
      }
    } else {
      const sid = notificationIdStr(notification);
      const newSeen = [...seenNotificationIds, sid];
      setSeenNotificationIds(newSeen);
      localStorage.setItem("seenNotificationIds", JSON.stringify(newSeen));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllRead();

      // Update local seen IDs
      const allIds = notifications.map((n) => notificationIdStr(n));
      setSeenNotificationIds(allIds);
      localStorage.setItem('seenNotificationIds', JSON.stringify(allIds));

      dataRefresh.emit("notifications");
      setNotificationsOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleViewAllNotifications = () => {
    setNotificationsOpen(false);
    onDismissPerfil?.();
    navigate('/notifications');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarUrl = (username: string) => {
    // Use custom avatar from backend if available, otherwise fallback to dicebear
    if (customAvatarUrl) return customAvatarUrl;
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`;
  };

  useEffect(() => {
    return () => {
      if (startClickTimerRef.current) clearTimeout(startClickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!startMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStartMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [startMenuOpen]);

  useEffect(() => {
    if (!startMenuOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (startMenuTriggerRef.current?.contains(t)) return;
      if (startMenuPanelRef.current?.contains(t)) return;
      setStartMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [startMenuOpen]);

  const flushStartClickBurst = () => {
    startClickTimerRef.current = null;
    const n = startClickCountRef.current;
    startClickCountRef.current = 0;
    if (n === 1) {
      setStartMenuOpen((v) => !v);
    } else if (n >= 2) {
      setStartMenuOpen(false);
      onDismissPerfil?.();
      navigate("/");
    }
  };

  const handleStartButtonClick = () => {
    startClickCountRef.current += 1;
    if (startClickTimerRef.current) clearTimeout(startClickTimerRef.current);
    startClickTimerRef.current = setTimeout(flushStartClickBurst, START_CLICK_DEBOUNCE_MS);
  };

  const handlePickModuleFromStartMenu = (route: string) => {
    setStartMenuOpen(false);
    if (openModuleByRoute) {
      openModuleByRoute(route);
    } else {
      onDismissPerfil?.();
      navigate(route);
    }
  };

  const closeStartMenuAnd = (fn: () => void) => {
    setStartMenuOpen(false);
    fn();
  };

  const handleModuleDragStart = (index: number) => {
    setDraggedModuleIndex(index);
  };

  const handleModuleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedModuleIndex === null || draggedModuleIndex === index) return;

    const newOrder = [...openModules];
    const draggedModule = newOrder[draggedModuleIndex];
    newOrder.splice(draggedModuleIndex, 1);
    newOrder.splice(index, 0, draggedModule);

    onReorderModules(newOrder);
    setDraggedModuleIndex(index);
  };

  const handleModuleDragEnd = () => {
    setDraggedModuleIndex(null);
  };

  const handleResetTestData = async () => {
    setIsResetting(true);
    try {
      const result = await systemConfigAPI.resetTestData();
      if (result.status === 'success') {
        const d = result.deleted;
        const parts: string[] = [];
        if (d) {
          if (d.facturas) parts.push(`${d.facturas} factura${d.facturas !== 1 ? 's' : ''}`);
          if (d.facturas_con_cae) {
            parts.push(`${d.facturas_con_cae} con CAE local`);
          }
          if (d.remitos) parts.push(`${d.remitos} remito${d.remitos !== 1 ? 's' : ''}`);
          if (d.recibos) parts.push(`${d.recibos} recibo${d.recibos !== 1 ? 's' : ''}`);
          const pagosProv = Number(d.proveedores_pagos ?? d.proveedoresPagos ?? 0);
          if (pagosProv) {
            parts.push(
              `${pagosProv} pago${pagosProv !== 1 ? 's' : ''} a proveedor`,
            );
          }
          if (d.cierres) parts.push(`${d.cierres} cierre${d.cierres !== 1 ? 's' : ''}`);
          if (d.cheques) parts.push(`${d.cheques} cheque${d.cheques !== 1 ? 's' : ''}`);
          if (d.cliente_notas) {
            parts.push(
              `${d.cliente_notas} nota${d.cliente_notas !== 1 ? 's' : ''} de cliente`,
            );
          }
          const logsWiped = Number(d.activity_logs ?? d.activityLogs ?? 0);
          if (logsWiped) {
            parts.push(
              `${logsWiped} registro${logsWiped !== 1 ? 's' : ''} de actividad`,
            );
          }
        }
        toast.success(
          parts.length
            ? `Datos de prueba vaciados (${parts.join(', ')}). Saldos en 0.`
            : (result.message || 'Datos de prueba locales vaciados.')
        );
        try {
          await reportesAPI.refreshDashboardSerie();
        } catch {
          // El dashboard POST-refresh al montar es el fallback.
        }
        window.location.href = '/';
      } else {
        toast.error("Error al reiniciar datos: " + result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error de conexión al intentar reiniciar datos");
    } finally {
      setIsResetting(false);
      setShowResetConfirm(false);
    }
  };

  const executeResetStock = async (wipeProductos: boolean, closeDialog: () => void) => {
    setIsStockOpLoading(true);
    try {
      const result = await systemConfigAPI.resetStock({ wipeProductos });
      if (result.status === 'success') {
        if (result.wipe_productos) {
          const total = result.deleted_product_rows ?? 0;
          toast.success(
            `Catálogo y stock reiniciados (${total.toLocaleString()} registro${total !== 1 ? 's' : ''} borrados en total; IDs reiniciados).`
          );
        } else {
          const n = result.deleted_stock_rows ?? 0;
          const m = result.deleted_movimiento_rows ?? 0;
          toast.success(
            `Solo stock vaciado (${n} existencia${n !== 1 ? 's' : ''}${m ? `, ${m} movimiento${m !== 1 ? 's' : ''}` : ''}; IDs reiniciados).`
          );
        }
        window.dispatchEvent(new CustomEvent("abs-stock-cleared"));
        clearSeenStockDynamicIds(setSeenNotificationIds);
        dataRefresh.emit("stock");
        dataRefresh.emit("notifications");
        void notificationsLiveStore.refresh(user.id || undefined, 0);
      } else {
        toast.error("Error: " + result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error de conexión");
    } finally {
      setIsStockOpLoading(false);
      closeDialog();
    }
  };

  const startMenuPortal =
    startMenuOpen &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <div
          className="animate-in fade-in-0 duration-75 fixed inset-0 z-[40] bg-black/25 backdrop-blur-[2px] dark:bg-black/40"
          aria-hidden
          onClick={() => setStartMenuOpen(false)}
        />
        <div
          ref={startMenuPanelRef}
          className={cn(
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-75 ease-out",
            "fixed left-1/2 top-[4.75rem] z-50 w-[min(calc(100vw-1.5rem),480px)] max-h-[min(calc(100vh-6rem),540px)]",
            "-translate-x-1/2",
            "flex flex-col overflow-hidden rounded-2xl border border-white/15",
            "bg-popover/95 text-popover-foreground shadow-2xl shadow-black/40 backdrop-blur-xl",
            "dark:border-white/10 dark:shadow-black/60"
          )}
          role="dialog"
          aria-modal="true"
          aria-label="Menú Inicio"
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]">
            {permittedModules.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">Sin módulos disponibles</p>
            ) : (
              <div className="grid grid-cols-3 gap-x-3 gap-y-2 sm:grid-cols-4 sm:gap-x-4 sm:gap-y-2">
                {permittedModules.map((mod) => {
                  const Icon = mod.icon;
                  const menuLabel = mod.shortName ?? mod.name;
                  return (
                    <button
                      key={mod.id}
                      type="button"
                      title={menuLabel}
                      aria-label={menuLabel}
                      onClick={() => handlePickModuleFromStartMenu(mod.route)}
                      onMouseEnter={() => prefetchModuleChunkByRoute(mod.route)}
                      onFocus={() => prefetchModuleChunkByRoute(mod.route)}
                      className={cn(
                        "group relative flex flex-col items-center rounded-xl",
                        "outline-none transition-colors duration-150",
                        "focus-visible:ring-2 focus-visible:ring-[#d97706]/50"
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex flex-col items-center rounded-xl px-2 pt-2 pb-1.5",
                          "transition-colors duration-150",
                          "group-hover:bg-white/10 group-focus-visible:bg-white/10"
                        )}
                      >
                        <div
                          className={cn(
                            "flex h-12 w-12 shrink-0 items-center justify-center rounded-[1rem] shadow-md transition-transform duration-150",
                            "bg-gradient-to-br",
                            mod.gradient,
                            "group-hover:scale-105 group-hover:shadow-lg"
                          )}
                        >
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <span
                          aria-hidden
                          className="pointer-events-none mt-1 flex h-4 w-full min-w-[3rem] max-w-[4.75rem] items-center justify-center overflow-hidden"
                        >
                          <span
                            className={cn(
                              "w-full px-0.5 text-center text-[10px] font-semibold leading-none sm:text-[11px]",
                              "truncate text-foreground/80",
                              "-translate-y-full opacity-0",
                              "transition-[transform,opacity] duration-200 ease-out",
                              "group-hover:translate-y-0 group-hover:opacity-100",
                              "group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
                            )}
                          >
                            {menuLabel}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-white/10 bg-gradient-to-r from-[#1a2744]/90 to-[#2d4a7c]/90 px-3 py-2.5 dark:from-[#1a2744]/95 dark:to-[#2d4a7c]/95">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <Avatar className="h-9 w-9 shrink-0 border border-white/15 shadow-md">
                  <AvatarImage src={getAvatarUrl(user.username)} alt={user.username} />
                  <AvatarFallback className="bg-white/10 text-xs text-white">{getInitials(user.username)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold capitalize leading-tight text-white">{user.username}</p>
                  {user.email ? (
                    <p className="truncate text-[11px] leading-tight text-white/60">{user.email}</p>
                  ) : null}
                  {user.rol ? (
                    <p className="truncate text-[10px] capitalize leading-tight text-white/50">{user.rol}</p>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-white hover:bg-white/15"
                  title="Perfil"
                  aria-label="Abrir perfil"
                  onClick={() => closeStartMenuAnd(onViewPerfil)}
                >
                  <User className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-white hover:bg-white/15"
                  title={isDarkMode ? "Modo claro" : "Modo oscuro"}
                  aria-label={isDarkMode ? "Activar modo claro" : "Activar modo oscuro"}
                  onClick={() => closeStartMenuAnd(toggleDarkMode)}
                >
                  {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-white hover:bg-rose-500/25 hover:text-white"
                  title="Salir"
                  aria-label="Cerrar sesión"
                  onClick={() => closeStartMenuAnd(onLogout)}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <>
    <nav className="relative z-50 bg-gradient-to-r from-[#1a2744] to-[#2d4a7c] border-b border-white/10">
      <div className="px-4 sm:px-6 py-2">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4 w-full min-h-[52px]">
          {/* Logo */}
          <div className="justify-self-start flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                onDismissPerfil?.();
                navigate('/');
              }}
              className="flex items-center hover:opacity-90 transition-opacity"
              title="ABS ERP System"
            >
              <img
                src="/logotipo-color-no-bg-no-slogan.png"
                alt="ABS"
                width={77}
                height={45}
                className="w-[77px] h-[45px] object-contain"
              />
            </button>
          </div>

          {/* Centrado estilo taskbar: Inicio fijo + módulos abiertos */}
          <div className="flex justify-center min-w-0 justify-self-stretch">
            <div className="flex items-center gap-1 sm:gap-2 min-w-0 max-w-full">
              <div className="relative flex-shrink-0 flex flex-col items-center" ref={startMenuTriggerRef}>
                <button
                  type="button"
                  onClick={handleStartButtonClick}
                  className="relative p-1 transition-all duration-300 transform hover:scale-110 active:scale-95"
                  title="Inicio — clic para menú, doble clic para escritorio"
                  aria-label="Abrir menú Inicio"
                  aria-expanded={startMenuOpen}
                  aria-haspopup="dialog"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow ring-1 ring-white/10">
                    <LayoutGrid className="h-4 w-4 text-white" strokeWidth={2.25} />
                  </div>
                </button>
                <div
                  className={`mt-1 h-[3px] rounded-full transition-all duration-300 ${location === "/" ? "w-4 bg-[#d97706] opacity-100 shadow-[0_0_8px_rgba(217,119,6,0.5)]" : "w-1.5 bg-white/30 opacity-100"}`}
                />
              </div>

              {openModules.length > 0 ? <div className="h-8 w-px flex-shrink-0 bg-white/10 mx-0.5" aria-hidden /> : null}

              <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto px-1 py-1 [scrollbar-width:thin]">
              {openModules.map((openModule, index) => {
                const Icon = openModule.module.icon;
                const isActive = location === openModule.module.route;
                return (
                  <div
                    key={openModule.id}
                    draggable
                    onDragStart={() => handleModuleDragStart(index)}
                    onDragOver={(e) => handleModuleDragOver(e, index)}
                    onDragEnd={handleModuleDragEnd}
                    className={`group relative cursor-move flex flex-col items-center transition-all duration-300 ${draggedModuleIndex === index ? 'opacity-30' : ''
                      }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onDismissPerfil?.();
                        navigate(openModule.module.route);
                      }}
                      onMouseEnter={() => prefetchModuleChunkByRoute(openModule.module.route)}
                      onFocus={() => prefetchModuleChunkByRoute(openModule.module.route)}
                      onDragStart={(e) => e.preventDefault()}
                      className="relative p-1 transition-all duration-300 transform group-hover:scale-110 active:scale-95 pointer-events-auto"
                      title={openModule.module.name}
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${openModule.module.gradient} flex items-center justify-center shadow-md transition-transform group-hover:shadow-lg`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                    </button>

                    {/* Active Indicator (Windows 11 line style) */}
                    <div className={`mt-1 h-[3px] rounded-full transition-all duration-300 ${isActive
                      ? 'w-4 bg-[#d97706] opacity-100 shadow-[0_0_8px_rgba(217,119,6,0.5)]'
                      : 'w-1.5 bg-white/30 opacity-100'
                      }`} />

                    {/* Close Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseModule(openModule.id);
                      }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-white/10 hover:bg-rose-500/80 text-white/50 hover:text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm border border-white/5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                );
              })}
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="justify-self-end flex flex-shrink-0 items-center gap-1 sm:gap-2">
            {showDemoResetActions && (
              <TooltipProvider delayDuration={300}>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-amber-300 hover:bg-white/10 hover:text-amber-200"
                        aria-label="Reiniciar datos de pruebas"
                        disabled={isResetting || isStockOpLoading}
                        onClick={() => setShowResetConfirm(true)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Reiniciar datos de pruebas</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-amber-300 hover:bg-white/10 hover:text-amber-200"
                        aria-label="Vaciar stock"
                        disabled={isResetting || isStockOpLoading}
                        onClick={() => setShowVaciateStockConfirm(true)}
                      >
                        <Package className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Vaciar stock</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-amber-300 hover:bg-white/10 hover:text-amber-200"
                        aria-label="Reiniciar stock y productos"
                        disabled={isResetting || isStockOpLoading}
                        onClick={() => setShowResetCatalogConfirm(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Reiniciar stock y productos</TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            )}
            {/* Notifications */}
            <DropdownMenu open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 text-white hover:bg-white/10"
                  aria-label="Abrir notificaciones"
                >
                  <Bell className="h-6 w-6" />
                  {hasUnseenNotifications && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notificaciones</span>
                  {hasUnseenNotifications && (
                    <span className="text-xs text-muted-foreground font-normal">
                      {bellNotifications.length} nueva{bellNotifications.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!hasUnseenNotifications ? (
                  <div className="p-8 text-sm text-muted-foreground text-center flex flex-col items-center gap-2">
                    <Bell className="h-8 w-8 opacity-20" />
                    <p>No hay notificaciones nuevas</p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {bellNotifications.slice(0, 5).map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        onSelect={(e) => {
                          if ((e.target as HTMLElement).closest("[data-notification-dismiss]")) {
                            e.preventDefault();
                            return;
                          }
                          handleNotificationClick(notification);
                        }}
                        className="flex items-start gap-2 p-3 cursor-pointer group"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {getSeverityIcon(notification.severity)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium line-clamp-1">
                            {notification.title}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        <button
                          type="button"
                          data-notification-dismiss
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => void handleDeleteNotification(e, notification)}
                          className={`flex-shrink-0 p-1 rounded-md transition-colors opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto ${deleteIconButtonClass}`}
                          title="Descartar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}

                {hasUnseenNotifications && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="flex gap-1 p-1">
                      <DropdownMenuItem
                        onClick={handleMarkAllAsRead}
                        className="flex-1 justify-center text-xs font-medium py-2"
                      >
                        <CheckCheck className="h-3.5 w-3.5 mr-1" />
                        Marcar todas como leídas
                      </DropdownMenuItem>
                    </div>
                  </>
                )}

                {notifications.length > 0 && (
                  <div className="p-1 border-t">
                    <DropdownMenuItem
                      onClick={handleViewAllNotifications}
                      className="justify-center text-xs font-medium text-primary py-2"
                    >
                      Ver centro de notificaciones
                    </DropdownMenuItem>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

          </div>
        </div>
      </div>

      {showDemoResetActions && (
        <>
          <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  Confirmar Reinicio de Datos
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>
                      Vacía <strong>localmente</strong> facturas y NC (incl. las que tengan CAE
                      local), remitos, recibos de cliente, pagos a proveedor (talonario
                      REC-PROV), presupuestos, cheques, fondos, movimientos de
                      caja, cierres diarios, notificaciones, notas de estado de cuenta de
                      clientes y el <strong>registro de actividad</strong> (Logs). Pone saldos
                      de clientes y proveedores en cero y reinicia numeradores locales
                      (incl. REC-PROV).
                    </p>
                    <p>
                      <strong>No</strong> borra productos, stock, maestros de clientes ni el
                      configurador de catálogo.
                      No llama FECAESolicitar: la próxima CAE consulta
                      FECompUltimoAutorizado del tipo enviado (last+1). Borra reservas
                      fiscales para no reusar un nro de otro tipo (10016). En homologación
                      AFIP no retrocede: pueden quedar CAEs huérfanos.
                    </p>
                    <p>
                      <strong>¿Continuar?</strong> No se puede deshacer.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isResetting}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleResetTestData();
                  }}
                  disabled={isResetting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {isResetting ? "Reiniciando..." : "Sí, Reiniciar Todo"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={showVaciateStockConfirm} onOpenChange={setShowVaciateStockConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-500">
                  <Package className="h-5 w-5" />
                  Vaciar solo stock
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminan todas las <strong>existencias</strong> (por depósito/sucursal) y los <strong>movimientos de stock</strong>.
                  Los <strong>productos</strong> del catálogo no se borran. Se <strong>reinician los IDs</strong> de esas tablas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isStockOpLoading}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void executeResetStock(false, () => setShowVaciateStockConfirm(false));
                  }}
                  disabled={isStockOpLoading}
                  className="bg-amber-700 hover:bg-amber-800 text-white dark:bg-amber-600"
                >
                  {isStockOpLoading ? "Procesando..." : "Sí, vaciar stock"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={showResetCatalogConfirm} onOpenChange={setShowResetCatalogConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-500">
                  <Package className="h-5 w-5" />
                  Reiniciar stock y productos
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Se borran <strong>todos los productos</strong> del catálogo (junto con existencias y movimientos de stock)
                  y se <strong>reinician los IDs</strong> de las tablas para que el próximo import empiece limpio.
                  En facturas o remitos viejos, la línea puede quedar sin producto enlazado.
                  <br /><br />
                  Los <strong>grupos/subgrupos</strong> de artículos no se eliminan. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isStockOpLoading}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void executeResetStock(true, () => setShowResetCatalogConfirm(false));
                  }}
                  disabled={isStockOpLoading}
                  className="bg-amber-700 hover:bg-amber-800 text-white dark:bg-amber-600"
                >
                  {isStockOpLoading ? "Procesando..." : "Sí, reiniciar todo"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </nav>
    {startMenuPortal}
    </>
  );
}